import { useEffect, useState } from "react";
import { Box, ButtonBase, CircularProgress, Paper, Skeleton, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import DownloadRounded from "@mui/icons-material/DownloadRounded";
import { observer } from "mobx-react";
import { ThemeColors } from "Theme/AppTheme";
import { isPowerPointArtifact, type AgentArtifact, type PowerPointArtifact } from "../../Models/AgentArtifact";
import FilesService from "services/FilesService";

const filesService = new FilesService();

export const AgentArtifacts = observer(function AgentArtifacts({
    artifacts,
}: {
    artifacts: AgentArtifact[];
}) {
    const powerPoints = artifacts.filter(isPowerPointArtifact);
    if (!powerPoints.length) return null;

    return (
        <Box display="flex" flexWrap="wrap" gap={1} width="100%">
            {powerPoints.map((artifact) => (
                <PowerPointArtifactCard
                    key={getArtifactCardKey(artifact)}
                    artifact={artifact}
                />
            ))}
        </Box>
    );
});

function getArtifactCardKey(artifact: PowerPointArtifact): string {
    // ArtifactId intentionally stays the same when a deck is revised. The
    // storage coordinates identify the specific revision shown by this card.
    return `${artifact.storageAccountId}:${artifact.directoryId}:${artifact.fileName}`;
}

const PowerPointArtifactCard = observer(function PowerPointArtifactCard({
    artifact,
}: {
    artifact: PowerPointArtifact;
}) {
    const [thumbnailUrl, setThumbnailUrl] = useState<string>();
    const [isThumbnailLoading, setIsThumbnailLoading] = useState(
        !!artifact.thumbnailFileName,
    );
    const [isDownloading, setIsDownloading] = useState(false);
    const [downloadedBytes, setDownloadedBytes] = useState(0);
    const [downloadComplete, setDownloadComplete] = useState(false);
    const [downloadFailed, setDownloadFailed] = useState(false);

    const { storageAccountId, directoryId, thumbnailFileName } = artifact;
    useEffect(() => {
        if (!thumbnailFileName) return;

        let cancelled = false;
        let objectUrl: string | undefined;
        setIsThumbnailLoading(true);
        void filesService
            .downloadAgentArtifact(storageAccountId, directoryId, thumbnailFileName)
            .then((blob) => {
                if (cancelled) return;
                objectUrl = URL.createObjectURL(blob);
                setThumbnailUrl(objectUrl);
            })
            .catch(() => {
                if (!cancelled) setThumbnailUrl(undefined);
            })
            .finally(() => {
                if (!cancelled) setIsThumbnailLoading(false);
            });

        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [storageAccountId, directoryId, thumbnailFileName]);

    async function downloadArtifact() {
        setIsDownloading(true);
        setDownloadedBytes(0);
        setDownloadComplete(false);
        setDownloadFailed(false);
        try {
            const blob = await filesService.downloadAgentArtifact(
                artifact.storageAccountId,
                artifact.directoryId,
                artifact.fileName,
                setDownloadedBytes,
            );
            const url = URL.createObjectURL(blob);
            const downloadLink = document.createElement("a");
            downloadLink.href = url;
            downloadLink.download = artifact.fileName;
            downloadLink.style.display = "none";
            document.body.appendChild(downloadLink);
            downloadLink.click();
            downloadLink.remove();
            window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
            setDownloadComplete(true);
        } catch {
            setDownloadFailed(true);
        } finally {
            setIsDownloading(false);
        }
    }

    return (
        <Paper
            variant="outlined"
            sx={{
                borderColor: "divider",
                borderRadius: 2,
                boxShadow: 1,
                maxWidth: 512,
                overflow: "hidden",
                width: "100%",
            }}
        >
            <ButtonBase
                aria-label={`Download ${artifact.name}`}
                disabled={isDownloading}
                onClick={downloadArtifact}
                sx={{
                    alignItems: "stretch",
                    display: "flex",
                    flexDirection: "column",
                    textAlign: "left",
                    width: "100%",
                }}
            >
                <Box
                    alignItems="center"
                    justifyContent="center"
                    sx={{
                        aspectRatio: "16 / 9",
                        bgcolor: ThemeColors.white,
                        display: "flex",
                        overflow: "hidden",
                        position: "relative",
                        width: "100%",
                    }}
                >
                    {thumbnailUrl ? (
                        <Box
                            component="img"
                            src={thumbnailUrl}
                            alt={`Preview of ${artifact.name}`}
                            sx={{
                                height: "100%",
                                inset: 0,
                                objectFit: "cover",
                                position: "absolute",
                                width: "100%",
                            }}
                        />
                    ) : isThumbnailLoading ? (
                        <Skeleton
                            aria-label="Loading preview"
                            animation="wave"
                            variant="rectangular"
                            sx={{ height: "100%", inset: 0, position: "absolute", width: "100%" }}
                        />
                    ) : (
                        <Typography variant="caption">PowerPoint</Typography>
                    )}
                    <Box
                        sx={{
                            bgcolor: alpha(ThemeColors.black, 0.82),
                            borderRadius: 1,
                            bottom: 8,
                            color: ThemeColors.white,
                            px: 1.5,
                            py: 0.75,
                            position: "absolute",
                            right: 8,
                        }}
                    >
                        <Typography color={ThemeColors.white} fontSize={13} fontWeight={600} lineHeight={1.2}>
                            {artifact.slideCount} {artifact.slideCount === 1 ? "slide" : "slides"}
                        </Typography>
                    </Box>
                </Box>
                <Box
                    alignItems="center"
                    display="flex"
                    gap={2}
                    justifyContent="space-between"
                    px={2}
                    py={1.5}
                    width="100%"
                >
                    <Box minWidth={0}>
                        <Typography fontSize={14} fontWeight={600} lineHeight={1.3} noWrap>
                            {artifact.name}
                        </Typography>
                        <Typography color="text.secondary" fontSize={13} lineHeight={1.4} noWrap>
                            PowerPoint · {formatFileSize(artifact.sizeBytes)} · Generated{" "}
                            {formatGeneratedDate(artifact.createdOn)}
                        </Typography>
                    </Box>
                    <Box
                        alignItems="center"
                        display="flex"
                        height={40}
                        justifyContent="center"
                        position="relative"
                        width={40}
                    >
                        {(isDownloading || downloadComplete) && (
                            <CircularProgress
                                aria-label="Downloading PowerPoint"
                                color="primary"
                                size={38}
                                thickness={3}
                                value={downloadComplete
                                    ? 100
                                    : artifact.sizeBytes > 0
                                        ? Math.min(100, (downloadedBytes / artifact.sizeBytes) * 100)
                                        : undefined}
                                variant={downloadComplete || artifact.sizeBytes > 0
                                    ? "determinate"
                                    : "indeterminate"}
                                sx={{ position: "absolute" }}
                            />
                        )}
                        <DownloadRounded
                            aria-hidden="true"
                            sx={{ color: "text.secondary", flexShrink: 0, fontSize: 24 }}
                        />
                    </Box>
                </Box>
            </ButtonBase>
            {downloadFailed && (
                <Typography color="error" display="block" px={2} pb={1.5} variant="caption">
                    Download failed. Try again.
                </Typography>
            )}
        </Paper>
    );
});

function formatFileSize(sizeBytes: number): string {
    if (!Number.isFinite(sizeBytes) || sizeBytes <= 0) return "Size unavailable";

    const units = ["B", "KB", "MB", "GB"];
    const unitIndex = Math.min(
        Math.floor(Math.log(sizeBytes) / Math.log(1024)),
        units.length - 1,
    );
    const value = sizeBytes / 1024 ** unitIndex;
    const fractionDigits = unitIndex >= 2 ? 1 : 0;
    return `${value.toFixed(fractionDigits)} ${units[unitIndex]}`;
}

function formatGeneratedDate(createdOn: string): string {
    const createdDate = new Date(createdOn);
    if (Number.isNaN(createdDate.getTime())) return "date unavailable";

    const today = new Date();
    const startOfDay = (date: Date) =>
        Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
    const daysAgo = Math.round(
        (startOfDay(today) - startOfDay(createdDate)) / 86_400_000,
    );

    if (daysAgo === 0) return "today";
    if (daysAgo === 1) return "yesterday";
    return createdDate.toLocaleDateString(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
    });
}
